import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Phone,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  Calendar,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const OUTCOMES = [
  { value: 'all', label: 'All Outcomes' },
  { value: 'Qualified', label: 'Qualified' },
  { value: 'Virtual Meet Scheduled', label: 'Virtual Meet Scheduled' },
  { value: 'Converted', label: 'Converted' },
  { value: 'Not Interested', label: 'Not Interested' },
  { value: 'Call Back Later', label: 'Call Back Later' },
  { value: 'No Answer', label: 'No Answer' },
  { value: 'Busy', label: 'Busy' },
  { value: 'Wrong Number', label: 'Wrong Number' },
  { value: 'Voicemail', label: 'Voicemail' },
];

const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

const getOutcomeColor = (outcome) => {
  const colors = {
    'Qualified': 'bg-green-100 text-green-800',
    'Virtual Meet Scheduled': 'bg-blue-100 text-blue-800',
    'Converted': 'bg-emerald-100 text-emerald-800',
    'Not Interested': 'bg-red-100 text-red-800',
    'Call Back Later': 'bg-yellow-100 text-yellow-800',
    'No Answer': 'bg-gray-100 text-gray-800',
    'Busy': 'bg-orange-100 text-orange-800',
    'Wrong Number': 'bg-red-100 text-red-700',
    'Voicemail': 'bg-purple-100 text-purple-800',
  };
  return colors[outcome] || 'bg-gray-100 text-gray-800';
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const CallLogsPage = () => {
  const { api, isManager } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('last_7_days');
  const [executives, setExecutives] = useState([]);
  const [executiveFilter, setExecutiveFilter] = useState('all');

  const fetchLogs = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setRefreshing(true);
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 25);
      params.append('date_preset', datePreset);
      
      if (outcomeFilter && outcomeFilter !== 'all') {
        params.append('outcome', outcomeFilter);
      }
      
      if (executiveFilter && executiveFilter !== 'all') {
        params.append('executive_id', executiveFilter);
      }

      const response = await api.get(`/calls/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch call logs:', error);
      toast.error('Failed to load call logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, page, outcomeFilter, datePreset, executiveFilter]);

  const fetchExecutives = useCallback(async () => {
    if (!isManager) return;
    try {
      const response = await api.get('/users/executives');
      setExecutives(response.data || []);
    } catch (error) {
      console.error('Failed to fetch executives:', error);
    }
  }, [api, isManager]);

  useEffect(() => {
    fetchLogs();
    fetchExecutives();
  }, [fetchLogs, fetchExecutives]);

  // Filter logs by search term (client-side)
  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.leadName?.toLowerCase().includes(search) ||
      log.leadMobile?.includes(search) ||
      log.summary?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16" />
        <Skeleton className="h-12" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="call-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
            Call Logs
          </h1>
          <p className="text-muted-foreground">
            {total} total calls recorded
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => fetchLogs(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg border">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by lead name, phone, or summary..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Select value={datePreset} onValueChange={(v) => { setDatePreset(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="date-filter">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_PRESETS.map(d => (
              <SelectItem key={d.value} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={outcomeFilter} onValueChange={(v) => { setOutcomeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="outcome-filter">
            <SelectValue placeholder="Filter by outcome" />
          </SelectTrigger>
          <SelectContent>
            {OUTCOMES.map(o => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isManager && (
          <Select value={executiveFilter} onValueChange={(v) => { setExecutiveFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[180px]" data-testid="executive-filter">
              <SelectValue placeholder="Filter by executive" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Executives</SelectItem>
              {executives.map(exec => (
                <SelectItem key={exec.id} value={exec.id}>
                  {exec.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Call Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Called By</TableHead>
                <TableHead>Date/Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No call logs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{log.leadName}</p>
                        <p className="text-sm text-muted-foreground">{log.leadMobile}</p>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[300px]">
                      <p className="truncate" title={log.summary}>
                        {log.summary}
                      </p>
                      <p className="text-xs text-muted-foreground truncate" title={log.nextAction}>
                        Next: {log.nextAction}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge className={getOutcomeColor(log.outcome)}>
                        {log.outcome}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatDuration(log.duration)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{log.calledBy}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {log.calledAt ? format(new Date(log.calledAt), 'MMM d, h:mm a') : '-'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallLogsPage;
